import type {
  Clinic,
  ClinicEvaluation,
  EvaluationPatch,
  FilterPreset,
  Interaction,
  Place,
  School,
  Settings,
} from "./types";

class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });
  if (res.status === 401) {
    onUnauthorized?.();
    throw new ApiError(401, "unauthorized");
  }
  if (!res.ok) {
    let msg = res.statusText;
    try {
      msg = (await res.json()).error || msg;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, msg);
  }
  if (res.headers.get("content-type")?.includes("application/json")) return res.json();
  return undefined as T;
}

export const api = {
  session: () => req<{ authed: boolean; admin: boolean }>("/session"),
  login: (password: string) =>
    req<{ authed: boolean; admin: boolean }>("/login", { method: "POST", body: JSON.stringify({ password }) }),
  logout: () => req<{ authed: boolean }>("/logout", { method: "POST" }),

  schools: () => req<{ schools: School[]; settings: Settings }>("/schools"),
  school: (id: string) => req<{ school: School }>(`/schools/${id}`),
  patchSchool: (id: string, patch: Partial<School>) =>
    req<{ school: School }>(`/schools/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  putEvaluation: (id: string, ev: EvaluationPatch) =>
    req<{ school: School }>(`/schools/${id}/evaluation`, {
      method: "PUT",
      body: JSON.stringify(ev),
    }),

  addInteraction: (id: string, entry: Partial<Interaction>) =>
    req<{ interactions: Interaction[] }>(`/schools/${id}/interactions`, {
      method: "POST",
      body: JSON.stringify(entry),
    }),
  patchInteraction: (id: string, iid: string, entry: Partial<Interaction>) =>
    req<{ interactions: Interaction[] }>(`/schools/${id}/interactions/${iid}`, {
      method: "PATCH",
      body: JSON.stringify(entry),
    }),
  deleteInteraction: (id: string, iid: string) =>
    req<{ interactions: Interaction[] }>(`/schools/${id}/interactions/${iid}`, { method: "DELETE" }),

  putSettings: (s: Partial<Settings>) =>
    req<{ settings: Settings }>("/settings", { method: "PUT", body: JSON.stringify(s) }),

  clinics: () => req<{ clinics: Clinic[]; places: Place[] }>("/clinics"),
  putClinicEval: (id: string, ev: Partial<Pick<ClinicEvaluation, "status" | "notes">>) =>
    req<{ clinic: Clinic }>(`/clinics/${id}/evaluation`, {
      method: "PUT",
      body: JSON.stringify(ev),
    }),

  filterPresets: () => req<{ presets: FilterPreset[] }>("/filters"),
  putFilterPresets: (presets: FilterPreset[]) =>
    req<{ presets: FilterPreset[] }>("/filters", {
      method: "PUT",
      body: JSON.stringify({ presets }),
    }),
};

export { ApiError };
